import { INIT, RESULT, STEP, VALUE } from "./propertyNames";
import { filter, remove } from "./transducers";
import { isReduced, reduced } from "./util"; // Transformers with no parameters, such as the one for count() here, are
// created the first time they are called so they can be tree shaken if unused.
// Tree shaking does not remove top-level object literal constants if they have
// computed keys.

var countTransformer;
export function count() {
  var _a;

  if (!countTransformer) {
    countTransformer = (_a = {}, _a[INIT] = function () {
      return 0;
    }, _a[RESULT] = function (result) {
      return result;
    }, _a[STEP] = function (result) {
      return result + 1;
    }, _a);
  }

  return countTransformer;
}
export function every(pred) {
  return remove(pred)(isEmpty());
}
export function find(pred) {
  return filter(pred)(first());
}
var firstTransformer;
export function first() {
  var _a;

  if (!firstTransformer) {
    firstTransformer = (_a = {}, _a[INIT] = function () {
      return null;
    }, _a[RESULT] = function (result) {
      return result;
    }, _a[STEP] = function (_, input) {
      return reduced(input);
    }, _a);
  }

  return firstTransformer;
}

var ForEachTransformer =
/**@__PURE__*/
function () {
  function ForEachTransformer(f) {
    this.f = f;
  }

  ForEachTransformer.prototype[INIT] = function () {
    return undefined;
  };

  ForEachTransformer.prototype[RESULT] = function () {
    return undefined;
  };

  ForEachTransformer.prototype[STEP] = function (_, input) {
    return this.f(input);
  };

  return ForEachTransformer;
}();

export function forEach(f) {
  return new ForEachTransformer(f);
}
var isEmptyTransformer;
export function isEmpty() {
  var _a;

  if (!isEmptyTransformer) {
    isEmptyTransformer = (_a = {}, _a[INIT] = function () {
      return true;
    }, _a[RESULT] = function (result) {
      return result;
    }, _a[STEP] = function () {
      return reduced(false);
    }, _a);
  }

  return isEmptyTransformer;
}

var JoinToString =
/**@__PURE__*/
function () {
  function JoinToString(separator) {
    this.separator = separator;
  }

  JoinToString.prototype[INIT] = function () {
    return [];
  };

  JoinToString.prototype[RESULT] = function (result) {
    return result.join(this.separator);
  };

  JoinToString.prototype[STEP] = function (result, input) {
    result.push(input);
    return result;
  };

  return JoinToString;
}();

export function joinToString(separator) {
  return new JoinToString(separator);
}
var isNotEmptyTransformer;
export function some(pred) {
  var _a;

  if (!isNotEmptyTransformer) {
    isNotEmptyTransformer = (_a = {}, _a[INIT] = function () {
      return false;
    }, _a[RESULT] = function (result) {
      return result;
    }, _a[STEP] = function () {
      return reduced(true);
    }, _a);
  }

  return filter(pred)(isNotEmptyTransformer);
}
var toArrayTransformer;
export function toArray() {
  var _a;

  if (!toArrayTransformer) {
    toArrayTransformer = (_a = {}, _a[INIT] = function () {
      return [];
    }, _a[RESULT] = function (result) {
      return result;
    }, _a[STEP] = function (result, input) {
      result.push(input);
      return result;
    }, _a);
  }

  return toArrayTransformer;
}

var ToMap =
/**@__PURE__*/
function () {
  function ToMap(getKey, getValue) {
    this.getKey = getKey;
    this.getValue = getValue;
  }

  ToMap.prototype[INIT] = function () {
    return new Map();
  };

  ToMap.prototype[RESULT] = function (result) {
    return result;
  };

  ToMap.prototype[STEP] = function (result, item) {
    result.set(this.getKey(item), this.getValue(item));
    return result;
  };

  return ToMap;
}();

export function toMap(getKey, getValue) {
  return new ToMap(getKey, getValue);
}

var InProgressTransformer =
/**@__PURE__*/
function () {
  function InProgressTransformer(xf) {
    this.xf = xf;
    this.isReduced = false;
    this.result = xf[INIT]();
  }

  InProgressTransformer.prototype.step = function (input) {
    if (!this.isReduced) {
      var newResult = this.xf[STEP](this.result, input);

      if (isReduced(newResult)) {
        this.result = newResult[VALUE];
        this.isReduced = true;
      } else {
        this.result = newResult;
      }
    }
  };

  InProgressTransformer.prototype.getResult = function () {
    return this.xf[RESULT](this.result);
  };

  return InProgressTransformer;
}();

var ToMapGroupBy =
/**@__PURE__*/
function () {
  function ToMapGroupBy(getKey, xf) {
    this.getKey = getKey;
    this.xf = xf;
  }

  ToMapGroupBy.prototype[INIT] = function () {
    return new Map();
  };

  ToMapGroupBy.prototype[RESULT] = function (result) {
    var completeResult = new Map();
    var entries = result.entries();

    for (var step = entries.next(); !step.done; step = entries.next()) {
      var _a = step.value,
          key = _a[0],
          value = _a[1];
      completeResult.set(key, value.getResult());
    }

    return completeResult;
  };

  ToMapGroupBy.prototype[STEP] = function (result, item) {
    var key = this.getKey(item);

    if (!result.has(key)) {
      result.set(key, new InProgressTransformer(this.xf));
    }

    result.get(key).step(item);
    return result;
  };

  return ToMapGroupBy;
}();

export function toMapGroupBy(getKey, transformer) {
  if (transformer === void 0) {
    transformer = toArray();
  }

  return new ToMapGroupBy(getKey, transformer);
}

var ToObject =
/**@__PURE__*/
function () {
  function ToObject(getKey, getValue) {
    this.getKey = getKey;
    this.getValue = getValue;
  }

  ToObject.prototype[INIT] = function () {
    return {};
  };

  ToObject.prototype[RESULT] = function (result) {
    return result;
  };

  ToObject.prototype[STEP] = function (result, item) {
    result[this.getKey(item)] = this.getValue(item);
    return result;
  };

  return ToObject;
}();

export function toObject(getKey, getValue) {
  return new ToObject(getKey, getValue);
}

var ToObjectGroupBy =
/**@__PURE__*/
function () {
  function ToObjectGroupBy(getKey, xf) {
    this.getKey = getKey;
    this.xf = xf;
  }

  ToObjectGroupBy.prototype[INIT] = function () {
    return {};
  };

  ToObjectGroupBy.prototype[RESULT] = function (result) {
    var completeResult = {};
    Object.keys(result).forEach(function (key) {
      return completeResult[key] = result[key].getResult();
    });
    return completeResult;
  };

  ToObjectGroupBy.prototype[STEP] = function (result, item) {
    var key = this.getKey(item);

    if (!result[key]) {
      result[key] = new InProgressTransformer(this.xf);
    }

    result[key].step(item);
    return result;
  };

  return ToObjectGroupBy;
}();

export function toObjectGroupBy(getKey, transformer) {
  if (transformer === void 0) {
    transformer = toArray();
  }

  return new ToObjectGroupBy(getKey, transformer);
}
var toSetTransformer;
export function toSet() {
  var _a;

  if (!toSetTransformer) {
    toSetTransformer = (_a = {}, _a[INIT] = function () {
      return new Set();
    }, _a[RESULT] = function (result) {
      return result;
    }, _a[STEP] = function (result, input) {
      result.add(input);
      return result;
    }, _a);
  }

  return toSetTransformer;
}
var averageTransformer;
export function toAverage() {
  var _a;

  if (!averageTransformer) {
    averageTransformer = (_a = {}, _a[INIT] = function () {
      return [0, 0];
    }, _a[RESULT] = function (result) {
      return result[1] === 0 ? null : result[0] / result[1];
    }, _a[STEP] = function (result, input) {
      result[0] += input;
      result[1]++;
      return result;
    }, _a);
  }

  return averageTransformer;
}

var Min =
/**@__PURE__*/
function () {
  function Min(comparator) {
    this.comparator = comparator;
  }

  Min.prototype[INIT] = function () {
    return null;
  };

  Min.prototype[RESULT] = function (result) {
    return result;
  };

  Min.prototype[STEP] = function (result, input) {
    return result === null || this.comparator(input, result) < 0 ? input : result;
  };

  return Min;
}();

function invertComparator(comparator) {
  return function (a, b) {
    return -comparator(a, b);
  };
}

var NATURAL_COMPARATOR = function NATURAL_COMPARATOR(a, b) {
  if (a < b) {
    return -1;
  } else {
    return a > b ? 1 : 0;
  }
};

export function max(comparator) {
  if (comparator === void 0) {
    comparator = NATURAL_COMPARATOR;
  }

  return new Min(invertComparator(comparator));
}
export function min(comparator) {
  if (comparator === void 0) {
    comparator = NATURAL_COMPARATOR;
  }

  return new Min(comparator);
}
var sumTransformer;
export function sum() {
  var _a;

  if (!sumTransformer) {
    sumTransformer = (_a = {}, _a[INIT] = function () {
      return 0;
    }, _a[RESULT] = function (result) {
      return result;
    }, _a[STEP] = function (result, input) {
      return result + input;
    }, _a);
  }

  return sumTransformer;
}